<template>
  <div
    id="related-videos"
    class="related-videos"
    data-ui-section="related-videos"
    :style="{backgroundColor: 'transparent' }"
  >
    <WebcallDataProvider
      :fetch-fn="getRelatedVideoTag"
      :initial-data="[]"
      @update="setDefaultActiveEndpoint"
    >
      <template
        slot-scope="{
          data: relatedVideoEndpoints
        }"
      >
        <div class="container pt-3 pt-md-0  m-0 w-100">
          <template>
            <keep-alive>
              <VerticalRecommendedVideos
                v-for="relatedVideoEndpoint in filterForActiveRelatedEndpoint({ relatedVideoEndpoints })"
                :key="relatedVideoEndpoint.id"
                :related-video-endpoint="relatedVideoEndpoint"

                @update="handleRelatedVideosUpdate"
              />
            </keep-alive>
          </template>
        </div>
      </template>
    </WebcallDataProvider>
  </div>
</template>

<script lang="ts">
import Vue, { PropType } from 'vue';
import * as VideoStore from '@/scripts/store/modules/video';
import WebcallDataProvider from '@/scripts/Components/Utilities/WebcallDataProvider';
import { VideoCollectionVideo } from '@/scripts/models/VideoCollectionVideo';
import { VideoRelatedEndpoint } from '@/scripts/models/VideoRelatedEndpoint';
import { SeriesVideoRelatedEndpoint } from '@/scripts/models/SeriesVideoRelatedEndpoint';
import { RecommendedVideoRelatedEndpoint } from '@/scripts/models/RecommendedVideoRelatedEndpoint';
import { ChannelVideoRelatedEndpoint } from '@/scripts/models/ChannelVideoRelatedEndpoint';
import {
  series as seriesType,
  seriesSeason as seriesSeasonType,
  channel as channelType,
  channelEdition as channelEditionType,
  channelEditionPlaylist as channelEditionPlaylistType,
  recommended as recommendedType,
} from '@/scripts/models/videoCollections/types';

import VerticalRecommendedVideos from './VerticalRecommendedVideos.ts.vue';
import { videosApi } from '@/scripts/webcall/v3/videos';

import { Model } from '@viddsee/server-api-client';

export default Vue.extend({
  components: {
    WebcallDataProvider,
    VerticalRecommendedVideos,
  },
  props: {
    video: {
      type: Object as PropType<Model.Video.Info>,
      required: true,
    },
  },

  data(): {
    activeEndpoint: null | VideoRelatedEndpoint;
    } {
    return {
      activeEndpoint: null,
    };
  },

  methods: {
    addWatchNextVideo: VideoStore.mapAction('addWatchNextVideo'),

    async getRelatedVideoTag(): Promise<VideoRelatedEndpoint[]> {
      const { data } = await videosApi.listRelatedEndpoints({
        videoId: this.video.id,
      });

      const items = data.items
        .filter(item => item.type === recommendedType)
        .map((videoEndpoint) => {
          switch (videoEndpoint.type) {
            case recommendedType:
              return new RecommendedVideoRelatedEndpoint(videoEndpoint);
            case seriesType:
            case seriesSeasonType:
              return new SeriesVideoRelatedEndpoint(videoEndpoint);
            case channelType:
            case channelEditionType:
            case channelEditionPlaylistType:
              return new ChannelVideoRelatedEndpoint(videoEndpoint);
            default:
              return new VideoRelatedEndpoint(videoEndpoint);
          }
        })
      ;

      // Suffle series / series_season item to the back
      const isFirstElementSeriesType = [
        seriesType,
        seriesSeasonType,
      ]
        .includes(items[0]?.type)
      ;
      if (isFirstElementSeriesType) {
        const firstItem = items.shift();
        items.push(firstItem as NonNullable<typeof firstItem>);
      }

      return items;
    },

    setDefaultActiveEndpoint({ data: relatedVideoEndpoints }: { data: VideoRelatedEndpoint[] }): void {
      if (this.activeEndpoint) return;

      this.updateActiveEndpoint({ relatedVideoEndpoint: relatedVideoEndpoints[0] });
    },

    updateActiveEndpoint({ relatedVideoEndpoint }: { relatedVideoEndpoint: VideoRelatedEndpoint }): void {
      this.activeEndpoint = relatedVideoEndpoint;
    },

    handleRelatedVideosUpdate({ videos }: { videos: Model.Video.Info[] }): void {
      const [video] = (videos || []);

      if (!video) {
        return;
      }

      this.addWatchNextVideo({
        videoCollectionVideo: new VideoCollectionVideo(video),
        skipIfExist: true,
      });
    },

    filterForActiveRelatedEndpoint({ relatedVideoEndpoints }: { relatedVideoEndpoints: VideoRelatedEndpoint[] }): VideoRelatedEndpoint[] {
      const activeEndpoint = this.activeEndpoint;
      if (activeEndpoint == null) {
        return [];
      }

      return relatedVideoEndpoints
        .filter(({ id }) => activeEndpoint.id === id)
      ;
    },
  },
});
</script>
